// PII redaction and endpoint normalization are the same operation here:
// query values are redacted unless explicitly allowed, hashes are dropped,
// and id-like path segments are masked to normalize per-resource endpoints.
// Page query names are retained for downstream urlspec required-param tracking,
// but only bounded, identifier-shaped names that do not look like IDs survive.
//
// Every URL leaving this package goes through here. Callers pick a class:
// "api" strips the query entirely, "asset" keeps only allowlisted keys, and
// "page" also keeps safe unknown keys with a fixed redacted value.
// Default is "api" — the strictest — so an unclassified call fails closed.

export type UrlClass = "api" | "page" | "asset";

const MASKED_SEGMENT_PATTERNS = [
  /^\d+$/,
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  /^[0-9a-f]{16,}$/i,
];

// Composite ids the patterns above miss — `0-63852086-CMNT-2655870` and
// friends. Measured against one day of production `captured_url` (2026-08-20,
// kr): length + charset alone also swallowed real screen names like
// `common_identity_v2`, so the digit ratio is what separates them (real ids
// ran 0.65~0.70, screen names 0.00~0.06).
const ID_MIN_LENGTH = 16;
const ID_MIN_DIGIT_RATIO = 0.3;
const ID_CHARSET = /^[A-Za-z0-9_-]+$/;

// Allowlisted values are enum-ish by construction (that is why they were
// allowlisted), so anything longer is an outlier we do not want on the wire —
// `referrer` is a clean enum for 1068 of its values and a full URL for the
// rest. Truncating is enough; the group key stays stable for the enum values.
const MAX_ALLOWED_VALUE_LENGTH = 32;
const REDACTED_QUERY_VALUE = "[redacted]";
const PAGE_QUERY_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_.\[\]-]{0,63}$/;
const MAX_PAGE_QUERY_KEYS = 30;

// Build-time injected by esbuild `define` from XVIEW_PERF_PAGE_QUERY_ALLOWLIST
// / XVIEW_PERF_ASSET_QUERY_ALLOWLIST. `typeof` guard keeps the source usable
// without the define (vitest, or a consumer importing src/ directly).
declare const __XVIEW_PERF_PAGE_QUERY_ALLOWLIST__: readonly string[];
declare const __XVIEW_PERF_ASSET_QUERY_ALLOWLIST__: readonly string[];

// Defaults derived from production data, not guesswork: keys carrying
// navigation/attribution signal with low distinct-value counts and no
// percent-encoded free text. Teams add to these via the env vars; this list is
// the floor, not the ceiling.
export const DEFAULT_PAGE_QUERY_ALLOWLIST: readonly string[] = [
  "referrer",
  "referer",
  "referrer_context",
  "referrer_screen_name",
  "referrer_area_name",
  "previous_screen_name",
  "previous_area_name",
  "previousScreenName",
  "previousAreaName",
  "funnelFrom",
  "queryFrom",
  "refer_root",
  "refer_content",
  "entrySection",
  "message_kind",
  "tab",
  "tabIndex",
  "tabKey",
  "feedHomeType",
  "sceneType",
  "serviceType",
  "featureType",
  "readonly",
  "is_root_webview",
  "utm_source",
  "utm_medium",
];

// Image CDN transform parameters — these explain why an LCP resource took as
// long as it did. Deliberately excludes `id`, `Signature`, `Expires`,
// `KeyName` (identifiers and signed-URL credentials).
export const DEFAULT_ASSET_QUERY_ALLOWLIST: readonly string[] = [
  "q",
  "quality",
  "s",
  "width",
  "height",
  "f",
  "format",
  "t",
  "type",
  "service",
];

const QUERY_ALLOWLISTS: Record<UrlClass, ReadonlySet<string>> = {
  // Never configurable. The API class is where the worst leakage lives
  // (receiver_nickname, sessionId, sig, GPS), so no env var may reopen it.
  api: new Set<string>(),
  page: new Set(
    typeof __XVIEW_PERF_PAGE_QUERY_ALLOWLIST__ !== "undefined"
      ? __XVIEW_PERF_PAGE_QUERY_ALLOWLIST__
      : DEFAULT_PAGE_QUERY_ALLOWLIST,
  ),
  asset: new Set(
    typeof __XVIEW_PERF_ASSET_QUERY_ALLOWLIST__ !== "undefined"
      ? __XVIEW_PERF_ASSET_QUERY_ALLOWLIST__
      : DEFAULT_ASSET_QUERY_ALLOWLIST,
  ),
};

function isIdSegment(segment: string): boolean {
  if (MASKED_SEGMENT_PATTERNS.some((pattern) => pattern.test(segment))) {
    return true;
  }
  if (segment.length < ID_MIN_LENGTH || !ID_CHARSET.test(segment)) return false;
  const digitCount = segment.replace(/\D/g, "").length;
  return digitCount / segment.length >= ID_MIN_DIGIT_RATIO;
}

// `slice` cuts UTF-16 code units, so a value whose 32nd unit is the high half
// of a surrogate pair (an emoji, a supplementary-plane CJK char) would end in a
// lone surrogate — and `encodeURIComponent` throws URIError on those. That
// throw would escape into the PerformanceTracker constructor and kill the whole
// SDK, so drop the orphan half. Only the trailing unit can be orphaned:
// URLSearchParams decodes with U+FFFD replacement and never hands back a lone
// surrogate of its own.
function truncate(value: string): string {
  if (value.length <= MAX_ALLOWED_VALUE_LENGTH) return value;
  const cut = value.slice(0, MAX_ALLOWED_VALUE_LENGTH);
  const last = cut.charCodeAt(cut.length - 1);
  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
}

// Sorted so `?tab=a&type=b` and `?type=b&tab=a` collapse to one group key.
// Re-encoded because URLSearchParams hands back decoded values, which could
// otherwise smuggle `&`/`=` into the wire string.
function keepAllowedQuery(
  params: URLSearchParams,
  allowlist: ReadonlySet<string>,
  keepPageKeys: boolean,
): string {
  if (!keepPageKeys && allowlist.size === 0) return "";
  let pageKeys: ReadonlySet<string> | undefined;
  if (keepPageKeys) {
    const keys = new Set<string>();
    params.forEach((_value, key) => {
      // Nested keys can carry identifiers too, e.g. user[01012345678].
      if (
        PAGE_QUERY_KEY_PATTERN.test(key) &&
        !isIdSegment(key) &&
        !key.split(/[.\[\]]/).some(isIdSegment)
      ) {
        keys.add(key);
      }
    });
    // Cap distinct keys after sorting so input order cannot change the group.
    pageKeys = new Set([...keys].sort().slice(0, MAX_PAGE_QUERY_KEYS));
  }
  const redactedKeys = new Set<string>();
  const kept: string[] = [];
  // forEach, not for-of: the package targets ios14/chrome77 without
  // downlevelIteration, and URLSearchParams is not typed as iterable there.
  params.forEach((value, key) => {
    if (pageKeys && !pageKeys.has(key)) return;
    if (!allowlist.has(key)) {
      if (!keepPageKeys || redactedKeys.has(key)) return;
      redactedKeys.add(key);
      value = REDACTED_QUERY_VALUE;
    }
    kept.push(
      `${encodeURIComponent(key)}=${encodeURIComponent(truncate(value))}`,
    );
  });
  if (kept.length === 0) return "";
  kept.sort();
  return `?${kept.join("&")}`;
}

export function sanitizeUrl(url: string, urlClass: UrlClass = "api"): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Resource timing names and location.href are absolute URLs; leave
    // anything else untouched.
    return url;
  }

  const pathname = parsed.pathname
    .split("/")
    .map((segment) => (isIdSegment(segment) ? ":id" : segment))
    .join("/");

  // Short-circuit before touching `searchParams` — reading it parses the whole
  // query string, and the api class (the hot path: up to 200 waterfall rows per
  // flush) discards every key anyway.
  const allowlist = QUERY_ALLOWLISTS[urlClass];
  const query =
    urlClass === "api" || (urlClass === "asset" && allowlist.size === 0)
      ? ""
      : keepAllowedQuery(parsed.searchParams, allowlist, urlClass === "page");

  return `${parsed.origin}${pathname}${query}`;
}
